// src/Login.jsx
import React, { useEffect, useState } from "react";
import "./Login.css";
import { auth, db } from "./firebase";
import {
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { FiUser } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";

const RESTAURANT_SIGNUP_NOTICE_KEY = "manidine_restaurant_signup_notice";

const safeTrim = (v) => String(v ?? "").trim();

const readSignupNotice = () => {
  try {
    const raw = localStorage.getItem(RESTAURANT_SIGNUP_NOTICE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn("[Login] failed to read signup notice:", err);
    return null;
  }
};

const clearSignupNotice = () => {
  try {
    localStorage.removeItem(RESTAURANT_SIGNUP_NOTICE_KEY);
  } catch (err) {
    console.warn("[Login] failed to clear signup notice:", err);
  }
};

const Login = () => {
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState({
    email: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [showResendVerification, setShowResendVerification] = useState(false);

  useEffect(() => {
    const storedNotice = readSignupNotice();
    if (
      storedNotice?.type === "restaurant_signup_verify_email" &&
      storedNotice?.email
    ) {
      setNotice(
        `We sent a verification email to ${storedNotice.email}. Verify that email first, then sign in to continue restaurant onboarding.`
      );
      setShowResendVerification(true);
      setFormValues((prev) => ({ ...prev, email: storedNotice.email }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUnverifiedLogin = async (user) => {
    const emailText = safeTrim(user?.email || formValues.email).toLowerCase();

    setShowResendVerification(true);
    setNotice(
      emailText
        ? `Your email is not verified yet. Check the inbox and spam folder for ${emailText}. If you did not receive it, resend the verification email below.`
        : "Your email is not verified yet. Check your inbox and spam folder. If you did not receive it, resend the verification email below."
    );
    setError(
      "Please verify your email before signing in. You can resend the verification email below."
    );

    await signOut(auth);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setShowResendVerification(false);
    setLoading(true);

    try {
      const userCred = await signInWithEmailAndPassword(
        auth,
        safeTrim(formValues.email).toLowerCase(),
        formValues.password
      );

      const user = userCred.user;
      await reload(user);
      await user.getIdToken(true);

      if (!user.emailVerified) {
        await handleUnverifiedLogin(user);
        return;
      }

      const restaurantRef = doc(db, "restaurants", user.uid);
      const restaurantSnap = await getDoc(restaurantRef);

      clearSignupNotice();

      if (restaurantSnap.exists()) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/complete-signup", { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = safeTrim(formValues.email).toLowerCase();
    const password = formValues.password;

    setError("");
    setNotice("");

    if (!email) {
      setError("Enter your email first.");
      return;
    }

    if (!password) {
      setError("Enter your password to resend the verification email.");
      return;
    }

    setResendLoading(true);

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      const user = userCred.user;

      await reload(user);

      if (user.emailVerified) {
        setNotice(
          `This email is already verified for ${email}. You can sign in now.`
        );
        setShowResendVerification(false);
        return;
      }

      await sendEmailVerification(user);
      setNotice(
        `Verification email sent again to ${email}. Check your inbox and spam folder, then sign in after verifying.`
      );
      setShowResendVerification(true);
    } catch (err) {
      console.error("Resend verification error:", err);
      setError(err?.message || "Failed to resend verification email.");
    } finally {
      try {
        await signOut(auth);
      } catch (signOutErr) {
        console.warn("[Login] signOut after resend failed:", signOutErr);
      }
      setResendLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = safeTrim(formValues.email).toLowerCase();

    setError("");
    setNotice("");

    if (!email) {
      setError("Enter your email first to reset your password.");
      return;
    }

    setResetLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setNotice(
        `Password reset email sent to ${email}. Check your inbox and spam folder.`
      );
    } catch (err) {
      console.error("Forgot password error:", err);
      setError(err?.message || "Failed to send password reset email.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoToSignup = () => {
    navigate("/signup");
  };

  return (
    <div className="auth-wrapper">
      <header className="auth-header">
        <div className="auth-brand">
          <span className="auth-logo">ManiDine for Restaurants</span>
          <span className="auth-logo-subtitle">Partner portal</span>
        </div>

        <div className="auth-header-actions">
          <button
            type="button"
            className="auth-header-link"
            onClick={handleGoToSignup}
          >
            <FiUser size={18} />
            Create account
          </button>
        </div>
      </header>

      <div className="auth-page">
        <div className="auth-left">
          <div className="auth-left-overlay">
            <p>
              A new era of honest, transparent, commission-free food delivery
            </p>
          </div>
        </div>

        <div className="auth-right">
          <div className="auth-card">
            <h2 className="auth-card-title">Welcome back</h2>
            <p className="auth-card-subtitle">
              Sign in to your ManiDine restaurant account.
            </p>

            {notice ? (
              <div
                style={{
                  background: "rgba(37, 99, 235, 0.08)",
                  border: "1px solid rgba(37, 99, 235, 0.18)",
                  color: "#1d4ed8",
                  borderRadius: 12,
                  padding: "0.85rem 1rem",
                  fontSize: "0.92rem",
                  marginBottom: "1rem",
                  lineHeight: 1.55,
                }}
              >
                {notice}
              </div>
            ) : null}

            {error && <p className="auth-error">{error}</p>}

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email</label>
                <input
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formValues.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formValues.password}
                  onChange={handleChange}
                  required
                />
              </div>

              <button
                type="submit"
                className="auth-primary-button"
                disabled={loading || resendLoading || resetLoading}
              >
                {loading ? "Signing in..." : "Submit"}
              </button>

              {showResendVerification ? (
                <button
                  type="button"
                  className="auth-link-button"
                  onClick={handleResendVerification}
                  disabled={loading || resendLoading || resetLoading}
                >
                  {resendLoading
                    ? "Sending verification email..."
                    : "Resend verification email"}
                </button>
              ) : (
                <button
                  type="button"
                  className="auth-link-button"
                  onClick={handleForgotPassword}
                  disabled={loading || resendLoading || resetLoading}
                >
                  {resetLoading ? "Sending reset email..." : "Forgot password?"}
                </button>
              )}
            </form>

            <p className="auth-small-text">
              Don’t have an account?{" "}
              <button
                type="button"
                className="inline-link-button"
                onClick={handleGoToSignup}
              >
                Create one
              </button>
            </p>

            <p className="auth-legal">
              By continuing, you agree to ManiDine’s{" "}
              <Link to="/terms">Terms</Link> and{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
